import '@/lib/axios';
import { basePath } from '@/lib/axios';
import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';

const appName = import.meta.env.VITE_APP_NAME || 'KodMail';

/** Reload once on vite:preloadError to pick up new manifest after deploy. */
const reloadGuardKey = 'kodmail.preload_reloaded_at';
window.addEventListener('vite:preloadError', (event) => {
    try {
        const last = Number(sessionStorage.getItem(reloadGuardKey) || 0);
        if (Date.now() - last < 10_000) {
            // Already reloaded recently; let error surface.
            return;
        }
        sessionStorage.setItem(reloadGuardKey, String(Date.now()));
    } catch {
        // sessionStorage blocked (private mode); reload anyway.
    }
    event.preventDefault();
    window.location.reload();
});

/** Subdirectory hosting: prefix Inertia visits + <a href> with basePath. */
if (basePath) {
    const prefix = (url) => {
        if (typeof url !== 'string') return url;
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) return url;
        if (! url.startsWith('/')) return url;
        if (url.startsWith(basePath + '/') || url === basePath) return url;
        return basePath + url;
    };

    // Patch Inertia router so visit/get/post/put/delete URLs go through the prefix.
    ['visit', 'get', 'post', 'put', 'patch', 'delete', 'reload'].forEach((m) => {
        const orig = router[m];
        if (typeof orig === 'function') {
            router[m] = function (url, ...rest) {
                return orig.call(router, prefix(url), ...rest);
            };
        }
    });

    // Intercept raw <a> clicks so non-Inertia links work too.
    document.addEventListener('click', (e) => {
        const a = e.target?.closest?.('a[href]');
        if (! a) return;
        const href = a.getAttribute('href');
        if (! href || ! href.startsWith('/') || href.startsWith('//')) return;
        if (href.startsWith(basePath + '/') || href === basePath) return;
        if (a.target === '_blank' || a.hasAttribute('download')) return;
        a.setAttribute('href', prefix(href));
    }, true);
}

/** Apply <html dir/lang> from shared i18n on each Inertia response. */
const applyDirection = (props) => {
    const i18n = props?.i18n ?? props?.page?.props?.i18n;
    if (! i18n || typeof document === 'undefined') return;
    document.documentElement.dir = i18n.rtl ? 'rtl' : 'ltr';
    if (i18n.locale) document.documentElement.lang = i18n.locale;
};
router.on('success', (event) => applyDirection(event.detail?.page?.props));

createInertiaApp({
    title: (title) => (title ? `${title} — ${appName}` : appName),
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.jsx`,
            import.meta.glob('./Pages/**/*.jsx'),
        ),
    setup({ el, App, props }) {
        applyDirection(props.initialPage?.props);
        createRoot(el).render(<App {...props} />);
    },
    progress: {
        // Fallback; live colour from --primary via CSS.
        color: '#EA6649',
        showSpinner: false,
    },
});
